import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  LinkOff as LinkOffIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";

import {
  copyText,
  formatExpiry,
  listShares,
  revokeShare,
  Share,
  shareUrl,
} from "./app/shares";

const STATE_COLOR: Record<
  Share["state"],
  "success" | "default" | "warning" | "error"
> = {
  active: "success",
  used: "default",
  expired: "warning",
  revoked: "error",
};

function ShareLinksDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    listShares()
      .then((s) => {
        setShares(s);
        setError(null);
      })
      .catch((e) => setError(e?.message ?? "Could not load links"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  async function handleRevoke(share: Share, purge: boolean) {
    const what = purge ? "Delete" : "Turn off";
    const name = share.key.split("/").pop();
    if (!window.confirm(`${what} the link for "${name}"?`)) return;
    setBusy(share.id);
    try {
      await revokeShare(share.id, purge);
      refresh();
    } catch (e: any) {
      setError(e?.message ?? "Could not update link");
    } finally {
      setBusy(null);
    }
  }

  async function handleCopy(share: Share) {
    if (await copyText(shareUrl(share.id))) {
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Share links</DialogTitle>
      <DialogContent sx={{ padding: 0, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : shares.length === 0 ? (
          <Typography textAlign="center" color="text.secondary" sx={{ py: 4 }}>
            No share links yet
          </Typography>
        ) : (
          <List dense>
            {shares.map((share) => (
              <ListItem
                key={share.id}
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={copiedId === share.id ? "Copied" : "Copy link"}>
                      <span>
                        <IconButton
                          edge={false}
                          size="small"
                          disabled={share.state !== "active"}
                          onClick={() => handleCopy(share)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Turn off now">
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          disabled={
                            busy === share.id || share.state !== "active"
                          }
                          onClick={() => handleRevoke(share, false)}
                        >
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete record">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={busy === share.id}
                          onClick={() => handleRevoke(share, true)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ pr: 12 }}
                    >
                      <Typography variant="body2" noWrap title={share.key}>
                        {share.key.split("/").pop()}
                      </Typography>
                      {share.oneTime && (
                        <Chip label="one-time" size="small" variant="outlined" />
                      )}
                      <Chip
                        label={share.state}
                        size="small"
                        color={STATE_COLOR[share.state]}
                      />
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatExpiry(share)} · {share.downloads}{" "}
                      {share.downloads === 1 ? "download" : "downloads"}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ShareLinksDialog;
